import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { ShieldCheck, FileText, Cookie, Trash2, Check, X, RefreshCw } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const PAGE = 50;

const DOC_SLUGS = [
  ['', 'All documents'],
  ['terms-of-service', 'Terms of Service'],
  ['privacy-policy', 'Privacy Policy'],
  ['cookie-policy', 'Cookie Policy'],
  ['earnings-disclaimer', 'Earnings Disclaimer'],
  ['refund-policy', 'Refund Policy'],
  ['member-agreement', 'Member Agreement'],
];

const fmt = (iso) => { try { return new Date(iso).toLocaleString(); } catch { return iso; } };

// Admin-only compliance area: policy acceptances (who accepted what version,
// when, from where), cookie consents, and the deletion-request review queue.
export default function CompliancePanel() {
  const { getAuthHeader } = useAuth();
  const [view, setView] = useState('acceptances');
  const [summary, setSummary] = useState(null);

  const [acceptances, setAcceptances] = useState({ total: 0, entries: [] });
  const [slug, setSlug] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);

  const [requests, setRequests] = useState([]);
  const [cookies, setCookies] = useState({ total: 0, entries: [] });
  const [cookiePage, setCookiePage] = useState(0);

  const loadSummary = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/compliance/summary`, { headers: getAuthHeader() });
      setSummary(res.data);
    } catch { /* chips stay hidden */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadAcceptances = useCallback(async () => {
    try {
      const params = new URLSearchParams({ limit: String(PAGE), skip: String(page * PAGE) });
      if (slug) params.set('slug', slug);
      if (search.trim()) params.set('search', search.trim());
      const res = await axios.get(`${API_URL}/api/admin/compliance/acceptances?${params}`, { headers: getAuthHeader() });
      setAcceptances(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load acceptances');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, search, page]);

  const loadRequests = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/compliance/deletion-requests`, { headers: getAuthHeader() });
      setRequests(res.data.requests || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load deletion requests');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadCookies = useCallback(async () => {
    try {
      const params = new URLSearchParams({ limit: String(PAGE), skip: String(cookiePage * PAGE) });
      const res = await axios.get(`${API_URL}/api/admin/compliance/cookie-consents?${params}`, { headers: getAuthHeader() });
      setCookies(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load cookie consents');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cookiePage]);

  useEffect(() => { loadSummary(); }, [loadSummary]);
  useEffect(() => { if (view === 'acceptances') loadAcceptances(); }, [view, loadAcceptances]);
  useEffect(() => { if (view === 'deletions') loadRequests(); }, [view, loadRequests]);
  useEffect(() => { if (view === 'cookies') loadCookies(); }, [view, loadCookies]);

  const resolve = async (req, status) => {
    const note = window.prompt(`${status === 'approved' ? 'Approve' : 'Deny'} deletion request from ${req.user_name || req.user_email}?\nOptional note:`) ;
    if (note === null) return;
    try {
      await axios.post(
        `${API_URL}/api/admin/compliance/deletion-requests/${req.id}/resolve`,
        { status, note },
        { headers: getAuthHeader() },
      );
      toast.success(`Request ${status}`);
      loadRequests();
      loadSummary();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to resolve request');
    }
  };

  const pending = requests.filter((r) => r.status === 'pending');
  const resolved = requests.filter((r) => r.status !== 'pending');

  const tabs = [
    { id: 'acceptances', label: 'Policy Acceptances', icon: FileText },
    { id: 'deletions', label: `Deletion Requests${summary?.pending_deletions ? ` (${summary.pending_deletions})` : ''}`, icon: Trash2 },
    { id: 'cookies', label: 'Cookie Consents', icon: Cookie },
  ];

  return (
    <div className="wm-card p-6 rounded-lg space-y-6" data-testid="compliance-panel">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-xl font-playfair font-bold text-foreground flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-gold" /> Compliance
          </h2>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            Legal-policy acceptances (member, version, date/time, IP), cookie consents, and the
            account-deletion review queue.
          </p>
        </div>
        {summary && (
          <div className="flex gap-2 flex-wrap text-xs" data-testid="compliance-summary-chips">
            <span className="px-3 py-1 rounded-full bg-white/5 text-muted-foreground">{summary.acceptances} acceptances</span>
            <span className="px-3 py-1 rounded-full bg-white/5 text-muted-foreground">{summary.cookie_consents} cookie consents</span>
            <span className={`px-3 py-1 rounded-full ${summary.pending_deletions ? 'bg-amber-500/15 text-amber-400' : 'bg-white/5 text-muted-foreground'}`}>
              {summary.pending_deletions} pending deletions
            </span>
          </div>
        )}
      </div>

      <div className="flex gap-2 flex-wrap border-b border-border pb-3">
        {tabs.map((t) => (
          <button
            key={t.id}
            onClick={() => setView(t.id)}
            className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full transition ${
              view === t.id ? 'bg-primary/15 text-primary font-medium' : 'text-muted-foreground hover:text-foreground'
            }`}
            data-testid={`compliance-tab-${t.id}`}
          >
            <t.icon className="w-3.5 h-3.5" /> {t.label}
          </button>
        ))}
      </div>

      {view === 'acceptances' && (
        <div className="space-y-4" data-testid="compliance-acceptances-view">
          <div className="flex gap-3 flex-wrap items-center">
            <Input
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(0); }}
              placeholder="Search member name or email…"
              className="max-w-xs bg-input border-border text-foreground"
              data-testid="compliance-acceptance-search"
            />
            <select
              value={slug}
              onChange={(e) => { setSlug(e.target.value); setPage(0); }}
              className="bg-input border border-border text-foreground text-sm rounded-md px-3 py-2"
              data-testid="compliance-acceptance-doc-filter"
            >
              {DOC_SLUGS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
            <Button size="sm" variant="outline" className="border-border" onClick={loadAcceptances} data-testid="compliance-acceptance-refresh">
              <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Refresh
            </Button>
            <span className="text-xs text-muted-foreground ml-auto">{acceptances.total} record(s)</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-muted-foreground">
                  <th className="py-2">Member</th><th className="py-2">Document</th><th className="py-2">Version</th><th className="py-2">Accepted at</th><th className="py-2">IP</th>
                </tr>
              </thead>
              <tbody>
                {acceptances.entries.length === 0 ? (
                  <tr><td colSpan={5} className="py-6 text-center text-muted-foreground">No acceptance records found.</td></tr>
                ) : acceptances.entries.map((a) => (
                  <tr key={`${a.user_id}-${a.doc_slug}-${a.version}`} className="border-b border-border/30">
                    <td className="py-2 text-foreground">
                      {a.user_name || a.user_email ? (
                        <>{a.user_name} <span className="text-muted-foreground text-xs">{a.user_email}</span></>
                      ) : (
                        <span className="text-muted-foreground text-xs italic">(deleted user · {String(a.user_id || '').slice(0, 8)})</span>
                      )}
                    </td>
                    <td className="py-2 text-foreground">{(DOC_SLUGS.find(([v]) => v === a.doc_slug) || [,'', a.doc_slug])[1] || a.doc_slug}</td>
                    <td className="py-2 text-muted-foreground">v{a.version}</td>
                    <td className="py-2 text-muted-foreground whitespace-nowrap">{fmt(a.accepted_at)}</td>
                    <td className="py-2 text-muted-foreground text-xs">{a.ip || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" className="border-border text-xs" disabled={page === 0} onClick={() => setPage(page - 1)} data-testid="compliance-acceptance-prev">Prev</Button>
            <Button size="sm" variant="outline" className="border-border text-xs" disabled={(page + 1) * PAGE >= acceptances.total} onClick={() => setPage(page + 1)} data-testid="compliance-acceptance-next">Next</Button>
          </div>
        </div>
      )}

      {view === 'deletions' && (
        <div className="space-y-4" data-testid="compliance-deletions-view">
          {pending.length === 0 ? (
            <p className="text-sm text-muted-foreground">No pending deletion requests.</p>
          ) : pending.map((r) => (
            <div key={r.id} className="border border-amber-500/30 bg-amber-500/5 rounded-lg p-4 flex items-start justify-between gap-4 flex-wrap" data-testid={`deletion-request-${r.id}`}>
              <div className="min-w-0">
                <p className="text-sm font-medium text-foreground">{r.user_name} <span className="text-muted-foreground text-xs">{r.user_email}</span></p>
                <p className="text-xs text-muted-foreground mt-1">Requested {fmt(r.created_at)}</p>
                {r.reason && <p className="text-xs text-foreground/80 mt-2">"{r.reason}"</p>}
              </div>
              <div className="flex gap-2 shrink-0">
                <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs" onClick={() => resolve(r, 'approved')} data-testid={`deletion-approve-${r.id}`}>
                  <Check className="w-3.5 h-3.5 mr-1" /> Approve
                </Button>
                <Button size="sm" variant="outline" className="border-red-500/40 text-red-400 text-xs" onClick={() => resolve(r, 'denied')} data-testid={`deletion-deny-${r.id}`}>
                  <X className="w-3.5 h-3.5 mr-1" /> Deny
                </Button>
              </div>
            </div>
          ))}
          <p className="text-[11px] text-muted-foreground">
            Approving marks the request only — actually removing the account stays a separate,
            deliberate admin action (settle balances first).
          </p>
          {resolved.length > 0 && (
            <div className="pt-2">
              <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Resolved</h4>
              {resolved.map((r) => (
                <div key={r.id} className="flex items-center justify-between gap-3 py-2 border-b border-border/30 text-xs">
                  <span className="text-foreground">{r.user_name || r.user_email}</span>
                  <span className={r.status === 'approved' ? 'text-red-400' : 'text-muted-foreground'}>
                    {r.status} · {r.resolved_at ? fmt(r.resolved_at) : ''}{r.note ? ` · ${r.note}` : ''}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {view === 'cookies' && (
        <div className="space-y-4" data-testid="compliance-cookies-view">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-muted-foreground">
                  <th className="py-2">Who</th><th className="py-2">Choice</th><th className="py-2">Analytics</th><th className="py-2">Marketing</th><th className="py-2">Policy v.</th><th className="py-2">Recorded</th><th className="py-2">IP</th>
                </tr>
              </thead>
              <tbody>
                {cookies.entries.length === 0 ? (
                  <tr><td colSpan={7} className="py-6 text-center text-muted-foreground">No cookie consents recorded yet.</td></tr>
                ) : cookies.entries.map((c) => (
                  <tr key={c.id} className="border-b border-border/30">
                    <td className="py-2 text-foreground">{c.user_email || <span className="text-muted-foreground">Visitor</span>}</td>
                    <td className="py-2 text-muted-foreground capitalize">{(c.action || '').replace('_', ' ')}</td>
                    <td className="py-2">{c.analytics ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <X className="w-3.5 h-3.5 text-muted-foreground" />}</td>
                    <td className="py-2">{c.marketing ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <X className="w-3.5 h-3.5 text-muted-foreground" />}</td>
                    <td className="py-2 text-muted-foreground">v{c.policy_version}</td>
                    <td className="py-2 text-muted-foreground whitespace-nowrap">{fmt(c.created_at)}</td>
                    <td className="py-2 text-muted-foreground text-xs">{c.ip || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" className="border-border text-xs" disabled={cookiePage === 0} onClick={() => setCookiePage(cookiePage - 1)} data-testid="compliance-cookies-prev">Prev</Button>
            <Button size="sm" variant="outline" className="border-border text-xs" disabled={(cookiePage + 1) * PAGE >= cookies.total} onClick={() => setCookiePage(cookiePage + 1)} data-testid="compliance-cookies-next">Next</Button>
            <span className="text-xs text-muted-foreground ml-2">{cookies.total} record(s)</span>
          </div>
        </div>
      )}
    </div>
  );
}
